import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts';
import { FeatureKey } from '../types/subscription';

interface LimitReachedModalProps {
  feature: FeatureKey;
  currentUsage: number;
  limitValue: number | null;
  onClose: () => void;
  isVisible: boolean;
}


export function LimitReachedModal({
  feature,
  currentUsage,
  limitValue,
  onClose,
  isVisible,
}: LimitReachedModalProps) {
  const navigate = useNavigate();
  const { t, i18n } = useLanguage();

  // Translate feature name
  const getFeatureName = (featureKey: FeatureKey): string => {
    const translations: Record<FeatureKey, string> = {
      'private_prompts': t('privatePrompts') || 'private prompts',
      'public_prompts': t('publicPrompts') || 'public prompts',
      'collections': t('collections') || 'collections',
      'api_calls': t('apiCalls') || 'api calls',
    };
    return translations[featureKey];
  };

  const featureName = getFeatureName(feature);

  const handleUpgradeClick = useCallback(() => {
    navigate('/billing');
  }, [navigate]);

  // ESC key to close modal (Accessibility)
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [handleKeyDown, isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="limit-guard-title"
      aria-describedby="limit-guard-description"
      data-testid="limit-guard-modal"
      onClick={(e) => {
        // Close modal when clicking outside
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white rounded-lg p-8 w-full max-w-md relative shadow-xl"
        role="document"
      >
      
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
          data-testid="limit-guard-close-button"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <h2
          id="limit-guard-title"
          className="text-2xl font-bold mb-4 text-brand-dark-blue pr-8"
          data-testid="limit-guard-title"
        >
          {t('limitGuard.limitReached')}
        </h2>
        <p
          id="limit-guard-description"
          className="text-gray-700 mb-6"
          data-testid="limit-guard-message"
        >
          {t('limitGuard.youveUsed', {
            current: currentUsage,
            limit: limitValue ?? 'unlimited',
            feature: featureName,
          })}
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-3 px-6 rounded-lg transition-colors"
            data-testid="limit-guard-close-button-secondary"
          >
            {t('close') || (i18n.language === 'es' ? 'Cerrar' : 'Close')}
          </button>
          <button
            onClick={handleUpgradeClick}
            className="flex-1 bg-brand-dark-blue hover:bg-brand-electric-blue text-white font-bold py-3 px-6 rounded-lg transition-colors"
            autoFocus
            data-testid="limit-guard-upgrade-button"
          >
            {t('limitGuard.upgradeNow')}
          </button>
        </div>
      </div>
    </div>
  );
}

